import { type FC, useEffect, useRef, useState } from 'react';

import { animated, useSpring } from '@react-spring/web';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { usePlaycoinWallet, useWalletState } from '@/entities/user/model/wallet.store';

import { formatNumber } from '@/shared/helpers/format-number';
import { getIsSlowDevice } from '@/shared/helpers/get-is-slow-device';
import { Lottie } from '@/shared/hocs/lottieWrapper';

import { ReactComponent as PlaycoinIcon } from '@/assets/images/tabs/playcoin.svg?react';

import coinsLottie from '@/assets/lotties/prizes/tab_coins.lottie?url';

import s from './PlaycoinAnimatedTab.module.scss';

interface Props {
  className?: string;
  amount: number;
  animate: boolean;
  onRest?: () => void;
}

const ANIMATION_DURATION_MS = 1600;
const ANIMATION_DELAY_MS = 100;

export const PlaycoinAnimatedTab: FC<Props> = ({
  className,
  amount,
  animate,
  onRest,
}) => {
  const { t } = useTranslation();

  const containerRef = useRef<HTMLDivElement>(null);

  const { pauseWalletWS } = useWalletState();
  const playcoinWallet = usePlaycoinWallet();

  const initialValue = Math.max(0, (playcoinWallet?.balance ?? 0) - amount);
  const targetValue = playcoinWallet?.balance ?? 0;

  const [isAnimating, setIsAnimating] = useState(false);
  const [showCoins, setShowCoins] = useState(false);

  const isSlowDevice = getIsSlowDevice();

  const [{ value }, api] = useSpring(() => ({
    value: initialValue,
    config: { duration: ANIMATION_DURATION_MS },
    onStart: () => setIsAnimating(true),
    onRest: ({ finished }) => {
      if (finished) {
        setIsAnimating(false);
        onRest?.();
      }
    },
    immediate: isSlowDevice,
  }));

  useEffect(() => {
    if (animate) {
      setShowCoins(true);

      setTimeout(() => {
        api.start({ value: targetValue });
      }, ANIMATION_DELAY_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animate]);

  useEffect(() => {
    api.start({ value: initialValue, immediate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  if (!playcoinWallet || pauseWalletWS) return null;

  return (
    <animated.div
      ref={containerRef}
      className={clsx(s.container, isAnimating && s.animating, className)}
      style={{
        scale: value.to({
          range: [
            initialValue,
            initialValue + (targetValue - initialValue) / 1.2,
            targetValue,
          ],
          output: [1, 1.2, 1],
          extrapolate: 'clamp',
        }),
      }}
    >
      <PlaycoinIcon className={s.icon} />
      <animated.span className={s.value}>
        {value.to((v) => formatNumber(v.toFixed(0), t))}
      </animated.span>
      <Lottie
        src={coinsLottie}
        loop={false}
        isPlaying={showCoins}
        className={s.lottie}
        onComplete={() => setShowCoins(false)}
      />
    </animated.div>
  );
};
